import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import { DialogContent, DialogActions } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useMembers } from '@app/api/members/useMembers';
import { fetchAllGroups } from '@app/store/slices/myTeamSlice';
import { ThemeButton } from '@shared/ui/buttons';
import {
  MemberRoleView,
  BasicSelectInput,
  BasicInput,
} from '@shared/ui/forms/inputs';
import { RenderItemTeam } from '@shared/ui/forms/select-render';
import Note from '@shared/ui/messages/Note';
import { Avatar } from '@shared/ui/parts';
import { icons } from '@shared/static/icons';
import { getTotalCards } from '@utils/helpers';
import { validateServerErrors } from '@utils/validation';
import { theme } from '@utils/libs/theme';

export default function MoveMemberDialog({ close, data }) {
  const { moveMember } = useMembers();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { allGroups } = useSelector((state) => state.my_team);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const currentGroup = data.groups?.[0];

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      currentGroup,
    },
  });

  const newGroupWatch = useWatch({
    control,
    name: 'group_id',
  });

  useEffect(() => {
    dispatch(fetchAllGroups());
  }, []);

  const cardOwners = allGroups
    .find((x) => x.id === currentGroup?.id)
    ?.users?.filter((x) => x.id !== data.id);

  async function onSubmit(formData) {
    const payload = {
      user_id: data.id,
      group_id: formData.group_id,
      new_card_owner_id: formData.new_card_owner_id,
    };

    try {
      setLoading(true);
      const res = await moveMember(payload);
      if (res) close();
    } catch (err) {
      console.error(err);
      validateServerErrors(setError, err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <DialogContent className="moveMember">
        <Note
          type="warning"
          marginBottom
          title={t('important')}
          content={t('move_member_dialog_content')}
        />
        <div className="form">
          <div className="formGroup-grid">
            <MemberRoleView member={data} label={t('member')} />
            <div></div>
          </div>
          {isMobile && <div className="form-divider"></div>}
          <div className="form-group-arrow">
            <BasicInput
              control={control}
              name="currentGroup"
              label={t('current_team')}
              error={errors.currentGroup}
              disabled
              isRequired={false}
            />
            <div className="arrow-icon">{icons['chevron-right']}</div>
            <BasicSelectInput
              control={control}
              name="group_id"
              error={errors.group_id}
              label={t('new_team')}
              cleanable={false}
              placeholder={t('select_team')}
              Render={RenderItemTeam}
              isRequired
              searchable
              data={allGroups.filter((x) => x.id !== currentGroup?.id)}
            />
          </div>

          {getTotalCards(data) > 0 && (
            <>
              {isMobile && <div className="form-divider"></div>}
              <div className="form-group-arrow">
                <BasicSelectInput
                  control={control}
                  name="new_card_owner_id"
                  label={t('new_cards_owner')}
                  icon="user-input"
                  placeholder={t('Administrator')}
                  searchable
                  data={cardOwners}
                />
                <div className="arrow-icon"></div>
                <div></div>
              </div>
            </>
          )}
          <div
            className={`moveMember__bottom ${newGroupWatch ? 'active' : ''}`}
          >
            <Avatar team={currentGroup} />
            <div className="line"></div>
            {icons['hugs']}
            <div className="line"></div>
            {newGroupWatch ? (
              <Avatar
                team={allGroups.find((group) => group.id === newGroupWatch)}
              />
            ) : (
              icons['unknown']
            )}
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <div className="btns-toolbar">
          <ThemeButton
            onClick={handleSubmit(onSubmit)}
            startIcon="transfer"
            loading={loading}
            block={false}
            minWidth
          >
            {t('move_member')}
          </ThemeButton>
          <div></div>
        </div>
      </DialogActions>
    </>
  );
}
